import React, { createContext, useContext, useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useMediaSession } from '../hooks/useMediaSession';
import { getOfflineObjectUrl } from '../lib/offline/offlineStore';
import { trackPodcastPlay, trackPodcastPause, trackPodcastComplete } from '../utils/analytics';

const PlayerContext = createContext(null);

/**
 * Owns the single shared `<audio>` element and every piece of state/behavior
 * that revolves around it — moved verbatim out of App.js (see task-3 brief):
 * playback state, the offline blob-URL resolution effect, playedTracks
 * persistence, ended-auto-advance, and the useMediaSession wiring.
 *
 * `playlist` = the queue (usePlaylist's currentPlaylist); `fallbackList` =
 * filteredPodcasts. next/prev walk `playlist` when non-empty, else
 * `fallbackList`, wrapping modulo — byte-equivalent to former App.js:260-278.
 */
export function PlayerProvider({ playlist, fallbackList, children }) {
  const [currentAudio, setCurrentAudio] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playedTracks, setPlayedTracks] = useState(new Set());
  const [isPlayerExpanded, setIsPlayerExpanded] = useState(false);

  // Shared audio state — lifted verbatim from App.js's player half.
  const audioRef = useRef(null);
  // Blob: URL for the currently-loaded DOWNLOADED episode (revoked on change).
  const blobUrlRef = useRef(null);
  // Latest play intent, read inside the async source-loader without re-subscribing.
  const isPlayingRef = useRef(isPlaying);
  isPlayingRef.current = isPlaying;
  // Pending absolute-seconds seek (from a deep link) applied once metadata loads.
  const pendingSeekRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.4);
  const [playbackRate, setPlaybackRate] = useState(1);

  // Load played tracks from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('playedTracks');
    if (saved) {
      try { setPlayedTracks(new Set(JSON.parse(saved))); } catch (_) {}
    }
  }, []);

  // Save played tracks
  useEffect(() => {
    if (playedTracks.size > 0) {
      localStorage.setItem('playedTracks', JSON.stringify(Array.from(playedTracks)));
    }
  }, [playedTracks]);

  // Sync volume to audio element (handles default + user changes)
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  /* ========== SHARED AUDIO MANAGEMENT (moved from App.js) ========== */

  // Load source when podcast changes. For a DOWNLOADED episode, play from a
  // blob: URL of the cached file (a complete, seekable, fully-offline copy)
  // instead of the network URL — letting the service worker range-serve cached
  // media is what made offline playback stall partway through. Falls back to
  // the network URL when the episode isn't downloaded.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !currentAudio) return;
    let cancelled = false;

    setCurrentTime(0);
    setDuration(0);

    (async () => {
      let src = currentAudio.s3Url;
      const offlineUrl = await getOfflineObjectUrl(currentAudio);
      if (cancelled) {
        if (offlineUrl) URL.revokeObjectURL(offlineUrl);
        return;
      }
      // Revoke the previous blob URL before swapping sources.
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = offlineUrl || null;
      if (offlineUrl) src = offlineUrl;
      audio.src = src;
      audio.load();
      // Resume playback once the (possibly async-resolved) source is set; the
      // play/pause sync effect can't, since src may not be ready when it runs.
      if (isPlayingRef.current) audio.play().catch(() => {});
    })();

    return () => { cancelled = true; };
  }, [currentAudio]);

  // Audio event listeners
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !currentAudio) return;

    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoadedMeta = () => {
      setDuration(audio.duration);
      audio.volume = volume;
    };
    const onEnded = () => {
      setIsPlaying(false);
      trackPodcastComplete(currentAudio.id, currentAudio.title, audio.duration);
      handleTrackComplete(currentAudio.id);
    };
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMeta);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);

    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMeta);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
    };
  }, [currentAudio]);

  // Play/pause sync
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !currentAudio) return;
    if (isPlaying) {
      // No-op on reject: the source may still be resolving (downloaded episodes
      // load their blob: URL asynchronously). The 'play'/'pause' listeners keep
      // isPlaying in sync with the element's real state.
      audio.play().catch(() => {});
    } else {
      audio.pause();
    }
  }, [isPlaying, currentAudio]);

  // Seek to a pending (deep-link) time once audio metadata loads.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !currentAudio || pendingSeekRef.current === null) return;
    const onReady = () => {
      if (pendingSeekRef.current !== null) {
        audio.currentTime = Math.min(pendingSeekRef.current, audio.duration);
        pendingSeekRef.current = null;
      }
    };
    audio.addEventListener('loadedmetadata', onReady);
    return () => audio.removeEventListener('loadedmetadata', onReady);
  }, [currentAudio]);

  // playPodcast: toggles play/pause when re-selecting the already-current
  // episode, otherwise switches to the new one and starts playing — verbatim
  // former App.js handlePlayPodcast.
  const playPodcast = useCallback((podcast) => {
    if (currentAudio && currentAudio.id === podcast.id) {
      setIsPlaying(!isPlaying);
    } else {
      setCurrentAudio(podcast);
      setIsPlaying(true);
    }
  }, [currentAudio, isPlaying]);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      if (currentAudio) trackPodcastPause(currentAudio.id, currentAudio.title, audio.currentTime);
    } else {
      audio.play().catch(() => {});
      if (currentAudio) trackPodcastPlay(currentAudio.id, currentAudio.title, audio.duration);
    }
  }, [isPlaying, currentAudio]);

  // seek(t): t is an absolute time in seconds. If metadata is already loaded
  // (the normal scrub-bar case — callers pass pct * duration), seek
  // immediately; otherwise (a deep link firing right after playPodcast, before
  // the new source's metadata has loaded) queue it for the effect above.
  const seek = useCallback((t) => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.duration && isFinite(audio.duration)) {
      audio.currentTime = Math.min(t, audio.duration);
    } else {
      pendingSeekRef.current = t;
    }
  }, []);

  const skipTime = useCallback((sec) => {
    const audio = audioRef.current;
    if (audio) audio.currentTime = Math.max(0, Math.min(audio.currentTime + sec, duration));
  }, [duration]);

  const changeVolume = useCallback((v) => {
    setVolume(v);
    if (audioRef.current) audioRef.current.volume = v;
  }, []);

  const changePlaybackRate = useCallback((r) => {
    setPlaybackRate(r);
    if (audioRef.current) audioRef.current.playbackRate = r;
  }, []);

  const closePlayer = useCallback(() => {
    const audio = audioRef.current;
    if (audio) audio.pause();
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    setCurrentAudio(null);
    setIsPlaying(false);
    setIsPlayerExpanded(false);
  }, []);

  const playNext = useCallback(() => {
    if (!currentAudio) return;
    const list = playlist.length > 0 ? playlist : fallbackList;
    if (!list || list.length === 0) return;
    const idx = list.findIndex(p => p.id === currentAudio.id);
    const next = list[(idx === -1 ? 0 : idx + 1) % list.length];
    setCurrentAudio(next);
    setIsPlaying(true);
  }, [currentAudio, playlist, fallbackList]);

  const playPrevious = useCallback(() => {
    if (!currentAudio) return;
    const list = playlist.length > 0 ? playlist : fallbackList;
    if (!list || list.length === 0) return;
    const idx = list.findIndex(p => p.id === currentAudio.id);
    const prev = list[idx <= 0 ? list.length - 1 : idx - 1];
    setCurrentAudio(prev);
    setIsPlaying(true);
  }, [currentAudio, playlist, fallbackList]);

  // Not memoized (verbatim from App.js's own handleTrackComplete) — read via
  // closure from the 'ended' listener above, which itself only re-subscribes
  // on [currentAudio], same staleness characteristics as before the move.
  const handleTrackComplete = (podcastId) => {
    setPlayedTracks(prev => new Set(prev.add(podcastId)));
    playNext();
  };

  const mediaHandlers = useMemo(() => ({
    play: () => { audioRef.current?.play().catch(() => {}); },
    pause: () => { audioRef.current?.pause(); },
    next: playNext,
    previous: playPrevious,
    seekBy: (sec) => skipTime(sec),
    seekTo: (sec) => { if (audioRef.current) audioRef.current.currentTime = sec; },
    stop: closePlayer,
  }), [playNext, playPrevious, closePlayer, skipTime]);

  useMediaSession({
    currentAudio,
    isPlaying,
    currentTime,
    duration,
    playbackRate,
    handlers: mediaHandlers,
  });

  /* ========== END SHARED AUDIO ========== */

  const value = useMemo(() => ({
    currentAudio,
    isPlaying,
    currentTime,
    duration,
    volume,
    playbackRate,
    isPlayerExpanded,
    playedTracks,
    playPodcast,
    togglePlay,
    seek,
    skipTime,
    setVolume: changeVolume,
    setPlaybackRate: changePlaybackRate,
    playNext,
    playPrevious,
    setExpanded: setIsPlayerExpanded,
    closePlayer,
  }), [
    currentAudio, isPlaying, currentTime, duration, volume, playbackRate, isPlayerExpanded, playedTracks,
    playPodcast, togglePlay, seek, skipTime, changeVolume, changePlaybackRate, playNext, playPrevious, closePlayer,
  ]);

  return (
    <PlayerContext.Provider value={value}>
      {/* Shared audio element — persists across mini/expanded player, and
          across route changes, for the life of the provider. */}
      <audio ref={audioRef} preload="metadata" playsInline crossOrigin="anonymous" style={{ display: 'none' }} />
      {children}
    </PlayerContext.Provider>
  );
}

export function usePlayer() {
  const ctx = useContext(PlayerContext);
  if (!ctx) throw new Error('usePlayer must be used within PlayerProvider');
  return ctx;
}
